import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { cn } from "@/lib/utils";
import { orpc } from "@/lib/orpc";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { MenusPager } from "./-components/menus-pager";
import { PantryAdd } from "./-components/pantry-add";
import { PantryChecked } from "./-components/pantry-checked";
import { PantryRow } from "./-components/pantry-row";
import { useKitchen } from "./-lib/use-kitchen";
import { usePaging } from "./-lib/use-paging";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/menus/pantry")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  component: PantryPage,
});

/** What is on the shelf: the shopping list takes it off what the menu needs. */
function PantryPage() {
  const { facilityId, canManage } = useKitchen();
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const paging = usePaging();
  const pantry = useQuery({
    ...orpc.agents.menus.pantry.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(q ? { search: q } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || pantry.isPlaceholderData;

  return (
    <section className="space-y-4">
      {canManage ? (
        <div className="max-w-3xl space-y-1.5">
          <h2 className="text-sm font-medium">Add to the shelf</h2>
          <PantryAdd facilityId={facilityId} />
        </div>
      ) : null}
      <SearchField
        className="max-w-xl sm:w-full"
        value={search}
        onChange={(next) => {
          setSearch(next);
          paging.reset();
        }}
        placeholder="Search the pantry"
      />
      {pantry.isPending ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : pantry.isError ? (
        <ReadFailed
          message="Couldn't load the pantry."
          error={pantry.error}
          onRetry={() => void pantry.refetch()}
        />
      ) : pantry.data.rows.length === 0 && searching ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : pantry.data.rows.length === 0 ? (
        <EmptyState
          title={search ? `Nothing on the shelf matches "${search.trim()}"` : "The pantry is empty"}
          description={
            search
              ? "Check the spelling, or add it above if it is on the shelf."
              : "Add what is on the shelf, and the shopping list takes it off what the menu needs."
          }
          action={
            search ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch("");
                  paging.reset();
                }}
              >
                Clear search
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div
          aria-busy={searching || undefined}
          className={cn(
            "bg-card shadow-card divide-y overflow-hidden rounded-xl border transition-opacity",
            searching && "opacity-60",
          )}
        >
          {pantry.data.checks.length > 0 || pantry.data.menuUseThrough ? (
            <div className="px-4 py-2">
              <PantryChecked
                checks={pantry.data.checks}
                menuUseThrough={pantry.data.menuUseThrough}
              />
            </div>
          ) : null}
          <ul className="divide-y">
            {pantry.data.rows.map((item) => (
              <PantryRow key={item.id} facilityId={facilityId} item={item} canManage={canManage} />
            ))}
          </ul>
          <MenusPager paging={paging} total={pantry.data.total} noun={["item", "items"]} />
        </div>
      )}
    </section>
  );
}
